import { ParsedSuggestion, SuggestionStatus } from '@dazzle/api-client';
import React, { createContext, PropsWithChildren, useCallback, useContext, useEffect, useRef, useState } from 'react';

import { suggestionGetInternal, suggestionGetReviewableInternal } from 'Common/api/DazzleInternalService';
import { fromSuggestion, USuggestion } from 'Common/util/Suggestion';

export interface IUFullSuggestion {
  suggestion: USuggestion;
  userName?: string;
  userPhoneNumber?: string;
  photoUrl?: string;
  address?: string;
}

interface IUSuggestionsContext {
  suggestions?: ParsedSuggestion[];
  firstPage: boolean;
  moreSuggestions: boolean;
  suggestionStatusFilter: SuggestionStatus;

  fetchSuggestions: (status?: SuggestionStatus, startAt?: number) => Promise<void>;
  getSuggestion: (suggestionId: string) => Promise<IUFullSuggestion | undefined>;
  setSuggestionTypeFilter: (status: SuggestionStatus) => void;
  nextPage: () => Promise<void>;
  previousPage: () => Promise<void>;
}

const SuggestionsContext = createContext<IUSuggestionsContext | null>(null);

const PAGE_SIZE = 10;

export const SuggestionsContextProvider = ({ children }: PropsWithChildren<object>) => {
  const [suggestions, setSuggestions] = useState<ParsedSuggestion[] | undefined>(undefined);
  const [suggestionStatusFilter, setSuggestionStatusFilter] = useState<SuggestionStatus>(
    SuggestionStatus.PendingReview
  );
  const [moreSuggestions, setMoreSuggestions] = useState(false);
  const [currentPageToken, setCurrentPageToken] = useState(0);
  const lastFetchedAt = useRef(0);

  const fetchSuggestionsImpl = async (status?: SuggestionStatus, startAt?: number) => {
    const page_token = `${startAt ?? currentPageToken}`;
    const iFetchedAt = (lastFetchedAt.current = Date.now());
    const response = await suggestionGetReviewableInternal({
      page_token,
      limit: PAGE_SIZE,
      status_filter: status ?? suggestionStatusFilter,
    });
    if (iFetchedAt !== lastFetchedAt.current) {
      return;
    }

    setSuggestions(response?.suggestions ?? []);
    setMoreSuggestions(response?.has_more ?? false);
    if (startAt !== undefined) {
      setCurrentPageToken(startAt);
    }
  };

  const fetchSuggestionsRef = useRef(fetchSuggestionsImpl);

  useEffect(() => {
    fetchSuggestionsRef.current = fetchSuggestionsImpl;
  });

  const fetchSuggestions = useCallback(async (status?: SuggestionStatus, startAt?: number) => {
    return fetchSuggestionsRef.current?.(status, startAt);
  }, []);

  const setSuggestionStatusFilterInternal = async (status: SuggestionStatus) => {
    setSuggestionStatusFilter(status);
    setSuggestions([]);
    await fetchSuggestions(status);
  };

  const getSuggestion = async (
    suggestionId: string
  ): Promise<IUFullSuggestion | undefined> => {
    const response = await suggestionGetInternal(suggestionId);

    if (response?.suggestion) {
      return {
        suggestion: fromSuggestion(response.suggestion),
        userName: response.user_full_name ?? undefined,
        userPhoneNumber: response.user_phone_number ?? undefined,
        photoUrl: response.product_photo_url ?? undefined,
        address: response.address ?? undefined,
      };
    }
    return undefined;
  };

  const nextPage = async () => {
    await fetchSuggestions(suggestionStatusFilter, currentPageToken + PAGE_SIZE);
  };

  const previousPage = async () => {
    await fetchSuggestions(suggestionStatusFilter, Math.max(0, currentPageToken - PAGE_SIZE));
  };

  const context = {
    firstPage: currentPageToken === 0,
    moreSuggestions,
    suggestions,
    suggestionStatusFilter,

    fetchSuggestions,
    getSuggestion,
    nextPage,
    previousPage,
    setSuggestionTypeFilter: setSuggestionStatusFilterInternal,
  };

  return <SuggestionsContext.Provider value={context}>{children}</SuggestionsContext.Provider>;
};

export const useSuggestionsContext = () => {
  const context = useContext(SuggestionsContext);
  if (context === null) {
    throw new Error('useSuggestionsContext must be used within a SuggestionsContextProvider');
  }
  return context;
};
